import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { NetworkPolicyProvider } from './contexts/NetworkPolicyContext';
import { BugReportsProvider } from './contexts/BugReportsContext';
import AppShellLayout from './components/AppShellLayout';
import PermissionGuard from './components/PermissionGuard';
import { PERMISSIONS } from './types/permissions';
import LandingPage from './pages/LandingPage';
import JobsDashboard from './pages/JobsDashboard';
import JobDetail from './pages/JobDetail';
import ExtensionsDashboard from './pages/ExtensionsDashboard';
import ExtensionDetail from './pages/ExtensionDetail';
import Reports from './pages/Reports';
import MultiStateReport from './pages/MultiStateReport';
import LogicAppReport from './pages/LogicAppReport';
import PeHealthReport from './pages/PeHealthReport';
import IntegrationConsole from './pages/IntegrationConsole';
import OperationsConsole from './pages/OperationsConsole';
import SnowflakeCostReport from './pages/SnowflakeCostReport';
import ErrorResolution from './pages/ErrorResolution';
import BadCchCodesReport from './pages/BadCchCodesReport';
import Outsourced from './pages/Outsourced';
import TokenManagement from './pages/admin/TokenManagement';
import DataIntegrity from './pages/admin/DataIntegrity';
import HealthCheck from './pages/admin/HealthCheck';
import Security from './pages/admin/Security';
import NetworkPolicies from './pages/admin/NetworkPolicies';
import TaxResearchRag from './pages/admin/TaxResearchRag';
import Sandbox from './pages/admin/Sandbox';
import DesksBoard from './pages/admin/DesksBoard';
import ZoomBroadcast from './pages/admin/ZoomBroadcast';
import DevTeam from './pages/admin/DevTeam';
import FollowUpHub from './pages/FollowUpHub';
// import ClientPush from './pages/ClientPush'; // Knuula Client Push — disabled
import ReturnAnalytics from './pages/ReturnAnalytics';
import MFiles2PEReconReport from './pages/MFiles2PEReconReport';
import Knuula2PEReconReport from './pages/Knuula2PEReconReport';
import Completed1040PaperSourceDocsReport from './pages/Completed1040PaperSourceDocsReport';
import POAListPage   from './pages/POA/POAListPage';
import POANewPage    from './pages/POA/POANewPage';
import POADetailPage from './pages/POA/POADetailPage';
import ContractorsPage from './pages/POA/ContractorsPage';
import AIReviewPage     from './pages/AIReview/AIReviewPage';
import AIReviewChatPage from './pages/AIReview/AIReviewChatPage';
import AskKeithPage     from './pages/AIReview/AskKeithPage';
import ImportPage       from './pages/Import/ImportPage';
import AddressCleansePage from './pages/Import/AddressCleansePage';
import UtilityMs990ExtensionPage    from './pages/Utility/UtilityMs990ExtensionPage';
import UtilityMemberStandardsPage  from './pages/Utility/UtilityMemberStandardsPage';
import PTODashboard from './pages/PTO/PTODashboard';
import AccessDenied from './pages/AccessDenied';

export default function App() {

  return (
    <AuthProvider>
      <PermissionsProvider>
        <NetworkPolicyProvider>
        <BugReportsProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/access-denied" element={<AccessDenied />} />

            <Route element={<AppShellLayout />}>
              {/* Landing — available to all authenticated users */}
              <Route path="/" element={<LandingPage />} />

              {/* Admin HQ (Portal) — requires nav.portal permission */}
              <Route path="/portal/jobs" element={
                <PermissionGuard permission={PERMISSIONS.NAV_PORTAL}><JobsDashboard /></PermissionGuard>
              } />
              <Route path="/portal/jobs/:returnId" element={
                <PermissionGuard permission={PERMISSIONS.NAV_PORTAL}><JobDetail /></PermissionGuard>
              } />
              <Route path="/portal/extensions" element={
                <PermissionGuard permission={PERMISSIONS.NAV_PORTAL}><ExtensionsDashboard /></PermissionGuard>
              } />
              <Route path="/portal/extensions/:returnId" element={
                <PermissionGuard permission={PERMISSIONS.NAV_PORTAL}><ExtensionDetail /></PermissionGuard>
              } />
              <Route path="/portal/followup" element={
                <PermissionGuard permission={PERMISSIONS.NAV_FOLLOWUP}><FollowUpHub /></PermissionGuard>
              } />
              {/* Knuula Client Push — disabled
              <Route path="/portal/clientpush" element={
                <PermissionGuard permission={PERMISSIONS.NAV_FOLLOWUP}><ClientPush /></PermissionGuard>
              } />
              */}

              <Route path="/reports" element={<Reports />} />
              <Route path="/reports/multistate" element={<MultiStateReport />} />
              <Route path="/reports/logicapps" element={<LogicAppReport />} />
              <Route path="/reports/pe-health" element={<PeHealthReport />} />
              <Route path="/reports/integration" element={<IntegrationConsole />} />
              <Route path="/reports/operations" element={<OperationsConsole />} />
              <Route path="/reports/snowflake-cost" element={<SnowflakeCostReport />} />
              <Route path="/reports/error-resolution" element={<ErrorResolution />} />
              <Route path="/reports/bad-cch-codes" element={<BadCchCodesReport />} />
              <Route path="/reports/return-analytics" element={<ReturnAnalytics />} />
              <Route path="/reports/mfiles2pe-recon" element={<MFiles2PEReconReport />} />
              <Route path="/reports/knuula2pe-recon" element={<Knuula2PEReconReport />} />
              <Route path="/reports/1040-paper-source-docs" element={<Completed1040PaperSourceDocsReport />} />
              <Route path="/outsourced" element={<Outsourced />} />

              <Route path="/poa"      element={<POAListPage />} />
              <Route path="/poa/new"  element={<POANewPage />} />
              <Route path="/poa/contractors" element={<ContractorsPage />} />
              <Route path="/poa/:id" element={<POADetailPage />} />

              <Route path="/ai-review"      element={<AIReviewPage />} />
              <Route path="/ai-review/chat" element={<AIReviewChatPage />} />
              <Route path="/ai-review/askkeith" element={
                <PermissionGuard permission={PERMISSIONS.NAV_ASK_KEITH}><AskKeithPage /></PermissionGuard>
              } />
              <Route path="/ai-review/askkeith/:sessionId" element={
                <PermissionGuard permission={PERMISSIONS.NAV_ASK_KEITH}><AskKeithPage /></PermissionGuard>
              } />

              <Route path="/import" element={
                <PermissionGuard permission={PERMISSIONS.NAV_IMPORT}><ImportPage /></PermissionGuard>
              } />

              <Route path="/import/addresses" element={
                <PermissionGuard permission={PERMISSIONS.NAV_IMPORT}><AddressCleansePage /></PermissionGuard>
              } />

              <Route path="/utility/ms990ext" element={
                <PermissionGuard permission={PERMISSIONS.NAV_IMPORT}><UtilityMs990ExtensionPage /></PermissionGuard>
              } />

              <Route path="/utility/member-standards" element={
                <PermissionGuard permission={PERMISSIONS.NAV_MEMBER_STANDARDS}><UtilityMemberStandardsPage /></PermissionGuard>
              } />

              <Route path="/pto" element={
                <PermissionGuard permission={PERMISSIONS.NAV_PTO}><PTODashboard /></PermissionGuard>
              } />

              <Route path="/admin/token-management" element={<TokenManagement />} />
              <Route path="/admin/desks" element={<DesksBoard key="leadership" />} />
              <Route path="/admin/team" element={<DesksBoard key="team" audience="team" />} />
              <Route path="/admin/data-integrity" element={<DataIntegrity />} />
              <Route path="/admin/health-check" element={<HealthCheck />} />
              <Route path="/admin/security" element={<Security />} />
              <Route path="/admin/network-policies" element={<NetworkPolicies />} />
              <Route path="/admin/tax-research-rag" element={<TaxResearchRag />} />
              <Route path="/admin/sandbox" element={<Sandbox />} />
              <Route path="/admin/zoom-broadcast" element={<ZoomBroadcast />} />
              <Route path="/admin/dev-team" element={<DevTeam />} />
              <Route path="/admin/animation-sandbox" element={<Navigate to="/admin/sandbox" replace />} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </BugReportsProvider>
        </NetworkPolicyProvider>
      </PermissionsProvider>
    </AuthProvider>
  );
}
